/* DeliveryZip — single source of truth for the buyer's delivery ZIP.
   Persists to localStorage + cart attribute "delivery_zip", validates against
   the bundled ZIP->state table, applies shipping_rules (metaobject JSON in
   [data-shipping-rules]) and updates every surface (header, PDP, cart) on the
   'zip:change' event. No frameworks. */
(function () {
  'use strict';

  var rulesEl = document.querySelector('[data-shipping-rules]');
  if (!rulesEl) return;
  var rules;
  try { rules = JSON.parse(rulesEl.textContent); } catch (e) { rules = {}; }
  if (!rules.enabled) return;

  var KEY = 'delivery-zip';
  var RATES_KEY = 'delivery-rates-cache';
  var stateTable = null;
  var statePromise = null;

  function getZip() {
    try { return localStorage.getItem(KEY) || ''; } catch (e) { return ''; }
  }

  function loadStates() {
    if (stateTable) return Promise.resolve(stateTable);
    /* inline same-origin copy first: a CDN fetch failing must never block buying */
    var inline = document.querySelector('[data-zip-states]');
    if (inline) {
      try {
        stateTable = JSON.parse(inline.textContent);
        return Promise.resolve(stateTable);
      } catch (e) {}
    }
    if (!statePromise) {
      var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 4000); });
      statePromise = Promise.race([
        fetch(rules.zipStatesUrl).then(function (r) { return r.json(); }),
        timeout
      ])
        .then(function (json) {
          if (json) stateTable = json;
          else statePromise = null; /* allow retry next time */
          return stateTable;
        })
        .catch(function () { statePromise = null; return null; });
    }
    return statePromise;
  }

  function stateFor(zip) {
    if (!stateTable || !/^\d{5}$/.test(zip)) return null;
    return stateTable[zip.slice(0, 3)] || null;
  }

  /* status: { code: 'ok' | 'ground' | 'excluded' | 'neutral', state, transit, threshold }.
     'neutral' = we can't classify the ZIP (table unavailable or unknown prefix):
     FAIL OPEN — show the ZIP without promises and keep commerce enabled. */
  function status(zip) {
    var threshold = rules.threshold;
    if ((rules.excludedZipPrefixes || []).indexOf(zip.slice(0, 3)) !== -1) {
      return { code: 'excluded', state: stateFor(zip), transit: null, threshold: threshold };
    }
    var state = stateFor(zip);
    if (!state) return { code: 'neutral', state: null, transit: transitFor(null), threshold: threshold };
    if ((rules.excludedStates || []).indexOf(state) !== -1) {
      return { code: 'excluded', state: state, transit: null, threshold: threshold };
    }
    var code = (rules.hazmatGroundOnlyStates || []).indexOf(state) !== -1 ? 'ground' : 'ok';
    return { code: code, state: state, transit: transitFor(state), threshold: threshold };
  }

  function transitFor(state) {
    var zones = rules.transitDaysByZone || {};
    var zone = state && rules.zoneByState ? rules.zoneByState[state] : null;
    return zones[zone] || zones['default'] || [2, 3];
  }

  function fill(template, vars) {
    return String(template || '').replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] != null ? vars[k] : m;
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Same as fill() but returns HTML with the ZIP wrapped in a 600-weight span.
     Everything is escaped first, so only our own span is markup. */
  function fillHtml(template, vars) {
    return escapeHtml(String(template || '')).replace(/\{(\w+)\}/g, function (m, k) {
      if (vars[k] == null) return m;
      var v = escapeHtml(vars[k]);
      return k === 'zip' ? '<span class="utility-bar__zip-value">' + v + '</span>' : v;
    });
  }

  /* Cache the computed state for first-paint rendering (head script +
     utility-bar inline hydrator read it before CSS/JS load). */
  function persistState(zip, st) {
    if (!zip || !st) return;
    try {
      localStorage.setItem('delivery-state', JSON.stringify({
        zip: zip,
        code: st.code,
        min: st.transit ? st.transit[0] : null,
        max: st.transit ? st.transit[1] : null,
        threshold: st.threshold
      }));
    } catch (e) {}
  }

  function setZip(zip, source) {
    zip = String(zip || '').replace(/\D/g, '').slice(0, 5);
    if (!/^\d{5}$/.test(zip)) return false;
    try { localStorage.setItem(KEY, zip); } catch (e) {}
    loadStates().then(function () {
      var st = status(zip);
      persistState(zip, st);
      /* both attributes so Liquid can server-render the state next load */
      var stateStr = st.code + (st.transit ? ':' + st.transit[0] + ':' + st.transit[1] : '');
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { delivery_zip: zip, delivery_state: stateStr } })
      }).catch(function () {});
    });
    broadcast(zip, source);
    return true;
  }

  function broadcast(zip, source) {
    if (!zip) {
      /* no ZIP -> no need to download the prefix table */
      document.dispatchEvent(new CustomEvent('zip:change', {
        detail: { zip: '', status: null, source: source || 'unknown' }
      }));
      return;
    }
    loadStates().then(function () {
      document.dispatchEvent(new CustomEvent('zip:change', {
        detail: { zip: zip, status: status(zip), source: source || 'unknown' }
      }));
    });
  }

  /* ---- delivery-date math lives in delivery-math.js (shared with scripts/delivery-date.test.js) ---- */
  var math = window.b2bDeliveryMath;

  function getItByRange(leadDays, transit, single, leadMax) {
    var r = math.computeDelivery({
      now: new Date(),
      cutoffTime: rules.cutoffTime,
      holidayDates: rules.holidayDates || [],
      leadDays: single ? Math.max(leadDays || 0, leadMax || 0) : leadDays,
      transitMin: transit ? transit[0] : 0,
      transitMax: transit ? transit[1] : 0
    });
    /* single = the latest date only ("get it by Tue, Oct 28"): cutoff + max handling + max transit, business days */
    return single ? math.formatDate(r.latest) : math.formatRange(r.earliest, r.latest);
  }

  /* ================= surfaces ================= */

  /* Header: message + icon state on every [data-zip-widget] */
  function renderHeader(zip, st) {
    document.querySelectorAll('[data-zip-widget]').forEach(function (widget) {
      var label = widget.querySelector('[data-delivery-message]');
      var money = '$' + Number(rules.threshold).toFixed(0);
      var html;
      if (!zip) {
        html = escapeHtml(widget.getAttribute('data-msg-empty') || 'Enter ZIP for delivery dates');
        widget.setAttribute('data-zip-status', 'empty');
      } else if (!st || st.code === 'neutral') {
        /* fail open: show the ZIP without promises, commerce stays enabled */
        html = 'Deliver to <span class="utility-bar__zip-value">' + escapeHtml(zip) + '</span>';
        widget.setAttribute('data-zip-status', 'ok');
      } else if (st.code === 'excluded') {
        html = fillHtml(widget.getAttribute('data-msg-unavailable') || "We don't ship to {zip} yet", { zip: zip });
        widget.setAttribute('data-zip-status', 'unavailable');
      } else if (st.code === 'ground') {
        html = fillHtml(widget.getAttribute('data-msg-ground') || 'Deliver to {zip} · Ground only, {min}–{max} days', { zip: zip, min: st.transit[0], max: st.transit[1], threshold: money });
        widget.setAttribute('data-zip-status', 'ground');
      } else {
        html = fillHtml(widget.getAttribute('data-msg-serviceable') || 'Deliver to {zip} · Free shipping over {threshold}', { zip: zip, threshold: money });
        widget.setAttribute('data-zip-status', 'ok');
      }
      if (label) label.innerHTML = html;
    });

    /* Center free-shipping slot: redundant once a serviceable ZIP shows the
       same message in the delivery item — hide it or swap in an alternate. */
    document.querySelectorAll('[data-center-shipping]').forEach(function (center) {
      var textEl = center.querySelector('[data-center-shipping-text]');
      var serviceable = !!(zip && st && (st.code === 'ok' || st.code === 'ground'));
      if (serviceable && center.getAttribute('data-when-zip') === 'alternate' && center.getAttribute('data-alternate-text')) {
        center.hidden = false;
        if (textEl) textEl.textContent = center.getAttribute('data-alternate-text');
      } else if (serviceable) {
        center.hidden = true;
      } else {
        center.hidden = false;
        if (textEl) textEl.textContent = center.getAttribute('data-default-text') || textEl.textContent;
      }
    });
    document.querySelectorAll('[data-delivery-zip-label]').forEach(function (el) {
      el.textContent = zip || 'Enter ZIP';
    });
    document.querySelectorAll('[data-delivery-zip-suffix]').forEach(function (el) {
      el.textContent = zip ? ' to ' + zip : '';
    });
  }

  /* PDP: get-it-by date, ground-only note, disable ATC on excluded ZIP */
  function renderPdp(zip, st) {
    var block = document.querySelector('[data-delivery-block]');
    if (!block) return;
    var target = block.querySelector('[data-get-it-by]');
    var note = block.querySelector('[data-zip-atc-note]');
    var lead = parseInt(block.getAttribute('data-lead') || '0', 10);
    var hazmat = block.hasAttribute('data-hazmat');
    var excl = [];
    try { excl = JSON.parse(block.getAttribute('data-exclude-states') || '[]'); } catch (e) {}

    var blocked = false;
    if (zip && st) {
      blocked = st.code === 'excluded' ||
        (hazmat && st.code === 'ground') ||
        (st.state && excl.indexOf(st.state) !== -1);
    }

    if (target) {
      /* no ZIP (or an unknown one): a dated estimate from the default transit zone when the block asks for it
         (cutoff + handling + default transit, business days); never on a blocked ZIP */
      var wantDefault = target.getAttribute('data-default-estimate') === '1';
      var single = target.getAttribute('data-single-date') === '1';
      var leadMax = parseInt(target.getAttribute('data-lead-max') || '0', 10);
      if (!blocked && wantDefault && (!zip || !st || st.code === 'neutral' || !st.transit)) {
        var dflt = transitFor(null);
        if (dflt && dflt[1]) {
          var dText = fill(target.getAttribute('data-template') || 'Get it by {date}', { date: getItByRange(lead, dflt, single, leadMax) });
          if (hazmat) { var dSuffix = target.getAttribute('data-ground-suffix') || ''; if (dSuffix) dText += ' ' + dSuffix; }
          target.textContent = dText;
          target.hidden = false;
        } else {
          target.hidden = true;
        }
      } else if (!zip || !st || blocked || !st.transit || st.code === 'neutral') {
        target.hidden = true;
      } else {
        var date = getItByRange(lead, st.transit, single, leadMax);
        var text = fill(target.getAttribute('data-template') || 'Get it by {date}', { date: date });
        if (hazmat) {
          var suffix = target.hasAttribute('data-ground-suffix') ? target.getAttribute('data-ground-suffix') : '· Ground only';
          if (suffix) text += ' ' + suffix;
        }
        target.textContent = text;
        target.hidden = false;
      }
    }
    document.dispatchEvent(new CustomEvent('delivery:updated'));
    /* blocked ZIP: the ships-in line must not still say "to {zip}" */
    block.querySelectorAll('[data-delivery-zip-suffix]').forEach(function (el) {
      el.textContent = blocked || !zip ? '' : ' to ' + zip;
    });
    if (note) {
      note.hidden = !blocked;
      if (blocked) {
        var msg = note.querySelector('[data-zip-atc-msg]');
        if (msg) msg.textContent = fill(note.getAttribute('data-msg') || "This item can't be delivered to {zip}.", { zip: zip });
      }
    }
    /* delivery.js owns only the ZIP flag: clearing a block must not re-enable a
       sold-out variant (Dawn's selected-variant JSON is the stock source of truth) */
    var soldOut = (function () {
      var vs = document.querySelector('.product__info-wrapper variant-selects [data-selected-variant]');
      if (!vs) return false; /* single-variant: Dawn owns the attribute */
      try { var v = JSON.parse(vs.textContent); return !(v && v.available); } catch (e) { return false; }
    })();
    document.querySelectorAll('.product-form__submit, [data-sticky-button]').forEach(function (btn) {
      if (blocked) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('data-zip-disabled', '');
      } else if (btn.hasAttribute('data-zip-disabled')) {
        btn.removeAttribute('data-zip-disabled');
        if (!soldOut && !btn.hasAttribute('data-size-pending')) btn.removeAttribute('disabled');
      }
    });
  }

  /* Cart: threshold-aware progress bar, line warnings, checkout gate,
     estimate auto-fill */
  function renderCart(zip, st) {
    document.querySelectorAll('[data-cart-summary]').forEach(function (summary) {
      var bar = summary.querySelector('.cart-shipping-bar');
      if (bar && bar.hasAttribute('data-cart-total')) {
        var total = parseInt(bar.getAttribute('data-cart-total'), 10); /* cents */
        var threshold = Math.round(Number(st && st.threshold != null ? st.threshold : rules.threshold) * 100);
        var remaining = threshold - total;
        var label = bar.querySelector('.cart-shipping-bar__label');
        var fillEl = bar.querySelector('.cart-shipping-bar__fill');
        if (label && fillEl && threshold > 0) {
          if (remaining > 0) {
            var moneySpan = label.querySelector('[data-remaining]');
            if (moneySpan) moneySpan.textContent = '$' + (remaining / 100).toFixed(2);
            fillEl.style.width = Math.min(100, Math.round((total / threshold) * 100)) + '%';
            fillEl.classList.remove('cart-shipping-bar__fill--full');
          } else {
            fillEl.style.width = '100%';
            fillEl.classList.add('cart-shipping-bar__fill--full');
          }
        }
      }
      var input = summary.querySelector('[data-estimate-zip]');
      if (input && zip && !input.value) input.value = zip;
    });

    /* unshippable lines */
    var anyBlocked = false;
    document.querySelectorAll('[data-line-ship-flags]').forEach(function (flag) {
      var blocked = false;
      if (zip && st) {
        if (st.code === 'excluded') blocked = true;
        if (!blocked && flag.hasAttribute('data-hazmat') && st.code === 'ground') blocked = true;
        if (!blocked && st.state) {
          try {
            var ex = JSON.parse(flag.getAttribute('data-exclude-states') || '[]');
            if (ex.indexOf(st.state) !== -1) blocked = true;
          } catch (e) {}
        }
      }
      var badge = flag.querySelector('[data-line-zip-warning]');
      if (badge) {
        badge.hidden = !blocked;
        if (blocked) badge.textContent = fill(rules.cartWarning || "Can't ship to {zip}", { zip: zip });
      }
      if (blocked) anyBlocked = true;
    });

    document.querySelectorAll('.cart-summary .cart__checkout-button').forEach(function (btn) {
      var blockedLabel = rules.checkoutBlockedLabel || 'Fix items to continue';
      if (!btn.hasAttribute('data-checkout-label')) btn.setAttribute('data-checkout-label', btn.textContent.trim());
      if (anyBlocked) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('data-zip-blocked', '');
        btn.textContent = blockedLabel;
      } else if (btn.hasAttribute('data-zip-blocked')) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('data-zip-blocked');
        btn.textContent = btn.getAttribute('data-checkout-label');
      }
    });

    /* cart-b2b.js's Estimate button does its own fetch; only auto-fill here.
       Skip while any line is unshippable — a rate would contradict the gate. */
    /* express pay must not route around the gate */
    document.querySelectorAll('.cart-summary .cart__dynamic-checkout-buttons').forEach(function (wallet) {
      wallet.hidden = anyBlocked;
    });

    /* recovery path: explanation + Change ZIP next to the gated button */
    document.querySelectorAll('[data-cart-zip-note]').forEach(function (note) {
      note.hidden = !anyBlocked;
      if (anyBlocked) {
        var msg = note.querySelector('[data-cart-zip-note-msg]');
        if (msg) msg.textContent = fill(note.getAttribute('data-msg') || "Some items can't be delivered to {zip}.", { zip: zip });
      }
    });

    if (anyBlocked) {
      document.querySelectorAll('[data-estimate-result]').forEach(function (res) { res.textContent = ''; });
    } else if (zip && st && st.code !== 'excluded' && document.querySelector('[data-cart-summary]')) {
      fetchRates(zip, st);
    }
  }

  /* /cart/shipping_rates.json with a TTL cache (per zip) */
  function fetchRates(zip, st) {
    /* the estimate result renders in both the drawer and page summaries */
    var outs = document.querySelectorAll('[data-estimate-result]');
    if (!outs.length) return;
    var write = function (text) {
      outs.forEach(function (out) { out.textContent = text; });
    };
    var ttlMs = (rules.ratesTtlMin || 10) * 60 * 1000;
    var cached = null;
    try { cached = JSON.parse(sessionStorage.getItem(RATES_KEY) || 'null'); } catch (e) {}
    if (cached && cached.zip === zip && Date.now() - cached.ts < ttlMs) {
      write(cached.text);
      return;
    }
    var qs = 'shipping_address%5Bzip%5D=' + zip +
      '&shipping_address%5Bcountry%5D=US&shipping_address%5Bprovince%5D=' + (st.state || '');
    fetch('/cart/shipping_rates.json?' + qs)
      .then(function (r) { return r.json(); })
      .then(function (json) {
        var ratesList = json.shipping_rates || [];
        if (!ratesList.length) {
          write('');
          return;
        }
        var cheapest = ratesList.reduce(function (a, b) {
          return parseFloat(a.price) <= parseFloat(b.price) ? a : b;
        });
        var price = parseFloat(cheapest.price);
        var text = 'Estimated shipping to ' + zip + ': ' +
          (price === 0 ? 'FREE' : '$' + price.toFixed(2)) + ' (' + cheapest.name + ')';
        write(text);
        try { sessionStorage.setItem(RATES_KEY, JSON.stringify({ zip: zip, ts: Date.now(), text: text })); } catch (e) {}
      })
      .catch(function () {});
  }

  function renderAll(detail) {
    var zip = detail ? detail.zip : getZip();
    var st = detail ? detail.status : (zip ? status(zip) : null);
    persistState(zip, st);
    renderHeader(zip, st);
    renderPdp(zip, st);
    renderCart(zip, st);
  }

  document.addEventListener('zip:change', function (e) { renderAll(e.detail); });
  /* other scripts (variant change re-render) can ask for a re-render with the saved ZIP */
  window.b2bDelivery = { refresh: function () { broadcast(getZip(), 'refresh'); } };
  /* cart re-renders (Section Rendering swaps) need a re-apply */
  if (window.subscribe) {
    window.subscribe('cart-update', function () { setTimeout(function () { broadcast(getZip(), 'cart-update'); }, 60); });
  }

  /* ---- popovers: header utility bar + any [data-zip-open] trigger ---- */
  function wirePopover(root) {
    var button = root.querySelector('.utility-bar__zip-button');
    var popover = root.querySelector('.utility-bar__zip-popover');
    if (!button || !popover || button.hasAttribute('data-zip-wired')) return;
    button.setAttribute('data-zip-wired', '');
    var input = popover.querySelector('input');
    var save = popover.querySelector('button[data-zip-save]') || popover.querySelector('.utility-bar__zip-save');
    function toggle(open) {
      var wasOpen = !popover.hidden;
      popover.hidden = !open;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) { input.value = getZip(); input.focus(); } else if (wasOpen) { button.focus(); }
    }
    button.addEventListener('click', function () { toggle(popover.hidden); });
    function commit() {
      if (setZip(input.value, 'header')) toggle(false);
      else input.focus();
    }
    if (save) save.addEventListener('click', commit);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); commit(); } });
    root._zipToggle = toggle;
  }
  document.querySelectorAll('[data-zip-widget]').forEach(wirePopover);

  /* one document-level pair for all popovers (avoids per-widget accumulation) */
  document.addEventListener('click', function (e) {
    document.querySelectorAll('[data-zip-widget]').forEach(function (root) {
      var popover = root.querySelector('.utility-bar__zip-popover');
      if (popover && !popover.hidden && !root.contains(e.target) && root._zipToggle) root._zipToggle(false);
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-zip-widget]').forEach(function (root) {
      var popover = root.querySelector('.utility-bar__zip-popover');
      if (popover && !popover.hidden && root._zipToggle) root._zipToggle(false);
    });
  });

  /* "Change ZIP" links and the cart estimate field also set the ZIP */
  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest('[data-zip-open]');
    if (openBtn) {
      e.preventDefault();
      var drawer = openBtn.closest('cart-drawer');
      if (drawer && typeof drawer.close === 'function') drawer.close();
      var widget = document.querySelector('[data-zip-widget]');
      var trigger = widget && widget.querySelector('.utility-bar__zip-button');
      if (trigger) {
        window.scrollTo({ top: 0 });
        trigger.click();
      }
    }
  });

  window.DeliveryZip = { get: getZip, set: setZip, status: status, refresh: function () { broadcast(getZip(), 'refresh'); } };

  /* initial paint */
  broadcast(getZip(), 'init');
})();
